"use client";

import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  IconButton,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import MailIcon from "@material-ui/icons/Mail";
import LockIcon from "@material-ui/icons/Lock";
import PersonIcon from "@material-ui/icons/Person";
import AppleIcon from "@material-ui/icons/Apple";
import CloseIcon from "@material-ui/icons/Close";
import loginBanner from "../../../assets/images/loginbanner.jpg";
import { login } from "../../../store/action";
import { useForm } from "react-hook-form";
import Notification from "../../../components/Notification/Notification";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1c487a",
    },
    secondary: {
      main: "#ffd54f",
    },
    background: {
      default: "#f5f3f0",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: "1.75rem",
      color: "#333333",
    },
    body1: {
      fontSize: "0.95rem",
      color: "#666666",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  shape: {
    borderRadius: 12,
  },
  overrides: {
    MuiTextField: {
      root: {
        marginBottom: "16px",
        "& .MuiOutlinedInput-root": {
          borderRadius: 25,
          backgroundColor: "#ffffff",
          "& fieldset": {
            borderColor: "#e0e0e0",
            borderWidth: 1,
          },
          "&:hover fieldset": {
            borderColor: "#bdbdbd",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#1c487a",
            borderWidth: 1,
          },
        },
        "& .MuiInputLabel-outlined": {
          color: "#999",
          fontSize: "0.875rem",
          "&.Mui-focused": {
            color: "#1c487a",
          },
        },
        "& .MuiOutlinedInput-input": {
          padding: "14px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 500,
        fontSize: "0.95rem",
        padding: "12px 24px",
        height: 48,
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
      },
      containedPrimary: {
        backgroundColor: "#ffd54f",
        color: "#333",
        "&:hover": {
          backgroundColor: "#ffcc00",
        },
      },
      outlined: {
        borderColor: "#e0e0e0",
        color: "#666",
        backgroundColor: "#ffffff",
        borderRadius: 25,
        "&:hover": {
          backgroundColor: "#f5f5f5",
          borderColor: "#bdbdbd",
        },
      },
    },
  },
});

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Login = () => {
  const dispatch = useDispatch();
  const { loading, loginErrors, userInfo } = useSelector(
    (state) => state.authReducer
  );
  const [loginNotify, setLoginNotify] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    dispatch(login(data));
    setLoginNotify(true);
  };

  const [data, setData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (event) => {
    setData({ ...data, [event.target.name]: event.target.value });
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        style={{
          minHeight: "100vh",
          backgroundColor: "#fafafb",
          padding: "20px",
         justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          container
          style={{
            maxWidth: "1700px",
            backgroundColor: "#fff",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            minHeight: "90vh",
          }}
        >
          {/* Left Side - Form */}
          <Grid
            item
            xs={12}
            md={4}
            style={{
              padding: "48px 40px",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Box style={{ maxWidth: "360px", width: "100%", margin: "0 auto" }}>
              {/* Notifications */}
              {loginNotify && (loginErrors?.message || loginErrors) && (
                <Notification
                  data={loginErrors?.message || loginErrors}
                  status="error"
                />
              )}
              {loginNotify && userInfo?.message && (
                <Notification data={userInfo?.message} status="success" />
              )}

              {/* Header */}
              <Box style={{ marginBottom: "32px" }}>
                <Typography
                  variant="h4"
                  style={{ fontWeight: 600, marginBottom: 8, color: "#333" }}
                >
                  Log in
                </Typography>
                <Typography style={{ color: "#666", fontSize: "0.95rem" }}>
                  Let's get started!
                </Typography>
              </Box>

              {/* Form */}
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  placeholder="amélie.laurent7622@gmail.com"
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <MailIcon
                        style={{ marginRight: 8, color: "#999", fontSize: 18 }}
                      />
                    ),
                  }}
                  {...register("email", {
                    required: "Please enter your email address",
                  })}
                  error={!!errors.email}
                />

                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  variant="outlined"
                  placeholder="••••••••••••••••••••"
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <LockIcon
                        style={{ marginRight: 8, color: "#999", fontSize: 18 }}
                      />
                    ),
                  }}
                  {...register("password", {
                    required: "Please enter your password",
                  })}
                  error={!!errors.password}
                  style={{ marginBottom: "24px", marginTop: "20px" }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                  style={{
                    marginBottom: "20px",
                    height: "48px",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  }}
                >
                  {loading && (
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                  )}
                  Login
                </Button>

                <Grid container spacing={2} style={{ marginBottom: "32px" }}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<AppleIcon />}
                      style={{ height: "48px" }}
                    >
                      Apple
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<GoogleIcon />}
                      style={{ height: "48px" }}
                    >
                      Google
                    </Button>
                  </Grid>
                </Grid>

                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography style={{ color: "#666", fontSize: "0.875rem" }}>
                    Have any account?{" "}
                    <Link
                      to="/login"
                      style={{
                        color: "#1c487a",
                        textDecoration: "underline",
                        fontWeight: 500,
                      }}
                    >
                      Sign in
                    </Link>
                  </Typography>
                  <Link
                    to="/terms"
                    style={{
                      color: "#666",
                      fontSize: "0.875rem",
                      textDecoration: "underline",
                    }}
                  >
                    Terms & Conditions
                  </Link>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Image */}
          <Grid
            item
            xs={12}
            md={8}
            style={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div style={{
              width: "100%",
              height: "100%",
              borderRadius: "25px",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundImage: `url(${loginBanner || "/placeholder.svg?height=600&width=600"})`
            }}>

              <Box
                style={{
                  position: "absolute",
                  top: "15%",
                  right: "8%",
                  backgroundColor: "#ffd54f",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  maxWidth: "200px",
                  zIndex: 1,
                }}
              >
                <Typography
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    fontSize: "0.875rem",
                    marginBottom: "2px",
                  }}
                >
                  Task Review With Team
                </Typography>
                <Typography style={{ color: "#666", fontSize: "0.75rem" }}>
                  09:30am-10:00am
                </Typography>
                <Typography style={{ color: "#666", fontSize: "0.75rem" }}>
                  09:30am-10:00am
                </Typography>
              </Box>

              <Box
                style={{
                  position: "absolute",
                  top: "35%",
                  right: "12%",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "12px",
                  padding: "12px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
                  minWidth: "140px",
                  zIndex: 1,
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <Typography
                        key={day}
                        style={{
                          fontSize: "0.7rem",
                          color: "#999",
                          fontWeight: 500,
                          width: "18px",
                          textAlign: "center",
                        }}
                      >
                        {day}
                      </Typography>
                    )
                  )}
                </Box>
                <Box style={{ display: "flex", justifyContent: "space-between" }}>
                  {["22", "23", "24", "25", "26", "27", "28"].map(
                    (date, index) => (
                      <Typography
                        key={date}
                        style={{
                          fontSize: "0.75rem",
                          color: index === 2 ? "#fff" : "#333",
                          fontWeight: 500,
                          width: "18px",
                          height: "18px",
                          textAlign: "center",
                          lineHeight: "18px",
                          backgroundColor:
                            index === 2 ? "#1c487a" : "transparent",
                          borderRadius: "50%",
                        }}
                      >
                        {date}
                      </Typography>
                    )
                  )}
                </Box>
              </Box>

              <Box
                style={{
                  position: "absolute",
                  bottom: "15%",
                  left: "8%",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "12px",
                  padding: "16px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  minWidth: "200px",
                  zIndex: 1,
                }}
              >
                <Typography
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    fontSize: "0.875rem",
                    marginBottom: "4px",
                  }}
                >
                  Daily Meeting
                </Typography>
                <Typography
                  style={{
                    color: "#666",
                    fontSize: "0.75rem",
                    marginBottom: "8px",
                  }}
                >
                  12:00am-01:00pm
                </Typography>
                <Box style={{ display: "flex", gap: "4px" }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Box
                      key={i}
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor:
                          i === 1
                            ? "#ff6b6b"
                            : i === 2
                              ? "#4ecdc4"
                              : i === 3
                                ? "#45b7d1"
                                : "#96ceb4",
                        border: "2px solid white",
                        marginLeft: i > 1 ? "-8px" : "0",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default Login;
